import { computed, makeObservable } from "mobx";
import { mergeDomainSuffixRestrictions, PageArgument, DomainSuffixRestriction, checkPermissionOrDomainSuffixRestriction, License } from "./License";

// Currently used to override licensing
export const weAreOfferingAllFeaturesToEveryone = true;

const mergeLicensesWithPermission = (licenses: License[]): boolean | DomainSuffixRestriction => {
	if (licenses.length === 0) return false;
	return mergeDomainSuffixRestrictions(licenses.map(license => license.validOnlyAtDomainSuffixes)) ?? true;
}

export abstract class LicensePermissions {
	abstract readonly validNonExpiredLicenses: License[];

	constructor() {
		// Need to use makeObservable and not @computed because this class is inherited
		makeObservable(this, {
			mayGenerateIpReports: computed,
			maySeeUnfilteredResults: computed,
			maySearchInboundTraffic: computed,
			mayUseForCommercialPurposes: computed,
			mayGenerateCausationGraph: computed,
			mayGenerateDetailedEvidence: computed,
			maySubmitToCourt: computed,
			mayExportData: computed,
			maySaveReports: computed,
			mayRunCrawler: computed,
		})
	}

	get mayGenerateIpReports() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.mayGenerateIpReports !== false));
	}
	get maySeeUnfilteredResults() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.maySeeUnfilteredResults !== false));
	}
	get mayUseForCommercialPurposes() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.mayUseForCommercialPurposes !== false));
	}
	get maySearchInboundTraffic() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.maySearchInboundTraffic !== false));
	}
	get mayGenerateCausationGraph() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.mayGenerateCausationGraph !== false));
	}
	get mayGenerateDetailedEvidence() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.mayGenerateDetailedEvidence !== false));
	}
	get maySubmitToCourt() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.maySubmitToCourt !== false));
	}
	get mayExportData() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.mayExportData !== false));
	}
	get maySaveReports() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.maySaveReports !== false));
	}
	get mayRunCrawler() {
		if (weAreOfferingAllFeaturesToEveryone) return true;
		return mergeLicensesWithPermission(this.validNonExpiredLicenses.filter(license => license.mayRunCrawler === true));
	}


	mayGenerateIpReportsAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.mayGenerateIpReports, pageArgument);
	};
	maySeeUnfilteredResultsAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.maySeeUnfilteredResults, pageArgument);
	};
	mayUseForCommercialPurposesAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.mayUseForCommercialPurposes, pageArgument);
	};
	maySearchInboundTrafficAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.maySearchInboundTraffic, pageArgument);
	};
	mayGenerateCausationGraphAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.mayGenerateCausationGraph, pageArgument);
	};
	mayGenerateDetailedEvidenceAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.mayGenerateDetailedEvidence, pageArgument);
	};
	maySubmitToCourtAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.maySubmitToCourt, pageArgument);
	};
	mayExportDataAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.mayExportData, pageArgument);
	};
	maySaveReportsAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.maySaveReports, pageArgument);
	};
	mayRunCrawlerAt = (pageArgument?: PageArgument) => {
		return checkPermissionOrDomainSuffixRestriction(this.mayRunCrawler, pageArgument);
	};

	// includesPageDestinationPair = (pageArgs: DestinationArgument & PageArgument) =>
	// 	// If this software is licensed and the license covers all results pertaining to this page,
	// 	// all pairs from that page should be included.
	// 	checkPermissionOrDomainSuffixRestriction(this.maySeeUnfilteredResults, pageArgs) ||
	// 	// This the page is not covered by a non-free license , we will
	// 	// probabilistically drop a fraction of the page/destination pairs.
	// 	ProbabilisticallyPermittedPageDestinationPairs.includes(pageArgs);

}

export class LicensePermissionsStatic extends LicensePermissions {
	constructor(public readonly validNonExpiredLicenses: License[]) {
		super();
		this.validNonExpiredLicenses = validNonExpiredLicenses;
	}
}

export interface HasLicensePermissions {
	licensePermissions: LicensePermissions;
}